import { parseBetweenBounds } from '@/shared/lib/data-field-bounds'

export interface DataFieldTypeOption {
  id: number
  dataFieldType: string
  dataFieldTypeDesc: string
}

// Legacy `maxlength="100"` on Field Label and Defined Value inputs.
export const DATA_FIELD_TEXT_MAX_LENGTH = 100
export const NUMBER_MAX_ACCEPTABLE_RULES = 1

export interface DataFieldValidationTypeOption {
  id: number
  validationType: string
  validationTypeDesc: string
}

export interface BaseAcceptableValueRow {
  value: string
  isAcceptable: boolean
  active: boolean
  operatorType?: 'OR' | 'AND' | ''
  instrumentTaskDataFieldValidationTypeId: string
}

export interface DataFieldTypeDefaults {
  instrumentTaskDataFieldTypeId: string
  required: boolean
  requiresValidation: boolean
  acceptableValues: BaseAcceptableValueRow[]
  definedValues: { value: string; isAcceptable: boolean }[]
}

export const resolveFieldTypeCode = (
  typeId: string,
  typeOptions: DataFieldTypeOption[],
): string | null => {
  if (!typeId) return null
  return typeOptions.find((option) => String(option.id) === typeId)?.dataFieldType ?? null
}

export const findValidationTypeId = (
  validationTypeCode: string,
  validationOptions: DataFieldValidationTypeOption[],
): string => {
  const match = validationOptions.find((option) => option.validationType === validationTypeCode)
  return match ? String(match.id) : ''
}

// Legacy parity (maintenanceTask.template.html):
// - defined: only the `defined` operator
// - number: excludes `defined`, and per the Acceptable/Unacceptable bug report UPD also
//   excludes `equals` and `range_bt` (Between) from the "Acceptable if" dropdown
// - linear: only the four threshold operators (GT / GT or Equal / LT / LT or Equal)
const NUMBER_EXCLUDED_VALIDATION_TYPES = ['defined']
const LINEAR_VALIDATION_TYPES = ['range_gt', 'range_gt_eq', 'range_lt', 'range_lt_eq']

export const validationOptionsForField = (
  typeCode: string | null,
  validationOptions: DataFieldValidationTypeOption[],
): DataFieldValidationTypeOption[] => {
  if (typeCode === 'defined') {
    return validationOptions.filter((option) => option.validationType === 'defined')
  }
  if (typeCode === 'linear') {
    return validationOptions.filter((option) =>
      LINEAR_VALIDATION_TYPES.includes(option.validationType),
    )
  }
  if (typeCode === 'number') {
    return validationOptions.filter(
      (option) => !NUMBER_EXCLUDED_VALIDATION_TYPES.includes(option.validationType),
    )
  }
  return validationOptions
}

export const isBetweenValidationType = (
  validationTypeId: string,
  validationOptions: DataFieldValidationTypeOption[],
): boolean => {
  const match = validationOptions.find((option) => String(option.id) === validationTypeId)
  return match?.validationType === 'range_bt'
}

export const validationTypeCodeFor = (
  validationTypeId: string,
  validationOptions: DataFieldValidationTypeOption[],
): string | null =>
  validationOptions.find((option) => String(option.id) === validationTypeId)?.validationType ?? null

export const shouldShowValidateField = (typeCode: string | null): boolean =>
  typeCode != null && !['text', 'textarea', 'fileAttachment'].includes(typeCode)

// Legacy parity: the Validate Field checkbox is checked-and-disabled for defined and
// linear types (their acceptable rules are always configured / carried from lots).
export const shouldDisableValidateField = (typeCode: string | null): boolean =>
  typeCode === 'defined' || typeCode === 'linear'

export const isDefinedValueType = (typeCode: string | null): boolean => typeCode === 'defined'

export const shouldShowDefinedValues = (typeCode: string | null): boolean => typeCode === 'defined'

export const canAddNumberAcceptableRule = (liveRuleCount: number): boolean =>
  liveRuleCount < NUMBER_MAX_ACCEPTABLE_RULES

export const buildDefaultAcceptableValues = (
  typeCode: string,
  validationOptions: DataFieldValidationTypeOption[],
): BaseAcceptableValueRow[] => {
  if (typeCode === 'number' || typeCode === 'linear') {
    return [
      {
        value: '',
        isAcceptable: true,
        active: true,
        instrumentTaskDataFieldValidationTypeId: findValidationTypeId(
          'range_gt',
          validationOptions,
        ),
      },
    ]
  }
  if (typeCode === 'defined') {
    return [
      {
        value: '',
        isAcceptable: false,
        active: true,
        instrumentTaskDataFieldValidationTypeId: findValidationTypeId('defined', validationOptions),
      },
    ]
  }
  return []
}

export const defaultValidationTypeIdForField = (
  typeCode: string | null,
  validationOptions: DataFieldValidationTypeOption[],
): string => {
  const filteredOptions = validationOptionsForField(typeCode, validationOptions)
  const defaultValidationCode =
    typeCode === 'defined'
      ? 'defined'
      : typeCode === 'number' || typeCode === 'linear'
        ? 'range_gt'
        : null
  return (
    (defaultValidationCode ? findValidationTypeId(defaultValidationCode, validationOptions) : '') ||
    (filteredOptions[0] ? String(filteredOptions[0].id) : '')
  )
}

export const defaultIsAcceptableForField = (typeCode: string | null): boolean =>
  typeCode !== 'defined'

export const patchForValidationTypeChange = (
  currentValue: string,
  previousValidationTypeId: string,
  newValidationTypeId: string,
  validationOptions: DataFieldValidationTypeOption[],
): Partial<Pick<BaseAcceptableValueRow, 'value'>> => {
  const betweenBounds = parseBetweenValue(currentValue)
  const wasBetween = isBetweenValidationType(previousValidationTypeId, validationOptions)
  const switchingToBetween = isBetweenValidationType(newValidationTypeId, validationOptions)

  if (switchingToBetween) {
    return { value: formatBetweenValue(betweenBounds.min, betweenBounds.max) }
  }
  if (wasBetween) {
    return { value: betweenBounds.min.trim() }
  }
  return {}
}

const parseBetweenValue = (value: string): { min: string; max: string } => {
  if (!value) return { min: '', max: '' }

  const delimiter = value.includes('..')
    ? '..'
    : value.includes(',')
      ? ','
      : value.includes('|')
        ? '|'
        : null
  if (!delimiter) return { min: value, max: '' }

  const [min, max] = value.split(delimiter)
  return { min: min?.trim() ?? '', max: max?.trim() ?? '' }
}

const formatBetweenValue = (min: string, max: string): string => {
  if (!min && !max) return ''
  return `${min}..${max}`
}

export const applyDataFieldTypeDefaults = (
  typeId: string,
  validationOptions: DataFieldValidationTypeOption[],
  typeOptions: DataFieldTypeOption[],
): DataFieldTypeDefaults => {
  const typeCode = resolveFieldTypeCode(typeId, typeOptions)
  if (!typeCode) {
    return {
      instrumentTaskDataFieldTypeId: typeId,
      required: false,
      requiresValidation: false,
      acceptableValues: [],
      definedValues: [],
    }
  }

  switch (typeCode) {
    case 'text':
    case 'textarea':
    case 'fileAttachment':
      return {
        instrumentTaskDataFieldTypeId: typeId,
        required: true,
        requiresValidation: false,
        acceptableValues: [],
        definedValues: [],
      }
    case 'defined':
    case 'linear':
      return {
        instrumentTaskDataFieldTypeId: typeId,
        required: true,
        requiresValidation: true,
        acceptableValues: buildDefaultAcceptableValues(typeCode, validationOptions),
        definedValues: typeCode === 'defined' ? [{ value: '', isAcceptable: false }] : [],
      }
    case 'number':
      return {
        instrumentTaskDataFieldTypeId: typeId,
        required: true,
        requiresValidation: true,
        acceptableValues: buildDefaultAcceptableValues('number', validationOptions),
        definedValues: [],
      }
    default:
      return {
        instrumentTaskDataFieldTypeId: typeId,
        required: true,
        requiresValidation: false,
        acceptableValues: [],
        definedValues: [],
      }
  }
}

// ── Single-"Between" primary + contradiction detection (REBUILD-515) ──────────
//
// Legacy exposes a single "Acceptable if: Between" condition that, when added via
// "Add More Validation", becomes the primary condition and discards any previously set
// conditions. On top of that, the editor must reject acceptable sets whose numeric rules
// can never be satisfied together (e.g. "> 16" AND "between 5 and 10").
//
// The helpers below are pure and number-aware. They operate on any row shape that carries
// a validation-type id, a value, and (optionally) a persistence `id` / `_destroy` flag, so
// both the editor rows and the modal's state rows can use them.

export interface AcceptableConditionRow {
  value: string
  instrumentTaskDataFieldValidationTypeId: string
  isAcceptable?: boolean
  // How the acceptable rules combine at evaluation time. Legacy stores this on the FIRST rule
  // and applies it to all (see AcceptableValueEvaluator#acceptable_from_rules): 'AND' = a value
  // must satisfy every rule; 'OR' (the default) = the first matching rule wins. Only 'AND' makes
  // the rules mutually constraining, so only 'AND' can produce a contradictory set.
  operatorType?: 'OR' | 'AND' | ''
  id?: string
  _destroy?: boolean
}

const isLiveRow = <T extends { _destroy?: boolean }>(row: T): boolean => row._destroy !== true

// Collapses the acceptable rows to a single primary "Between" row, matching legacy's
// single-Between semantics. Persisted rows (those with an `id`) that are superseded are
// kept but flagged `_destroy: true` so the backend removes them; unpersisted rows are
// dropped outright. The passed-in `betweenRow` is kept as the sole live condition.
export const enforceSingleBetween = <T extends AcceptableConditionRow>(
  rows: T[],
  betweenRow: T,
): T[] => {
  const result: T[] = []
  for (const row of rows) {
    if (row === betweenRow) {
      result.push(row)
      continue
    }
    if (row.id) {
      // Persisted → keep but mark for deletion (don't duplicate an already-destroyed row).
      result.push({ ...row, _destroy: true })
    }
    // Unpersisted non-primary rows are discarded entirely.
  }
  return result
}

// Applies the single-Between-primary rule when a Between condition is added or a row is
// switched to Between. Returns the collapsed rows (only the Between remains live). If no
// row in `rows` is a Between condition, the rows are returned unchanged.
export const applyBetweenPrimaryOnAdd = <T extends AcceptableConditionRow>(
  rows: T[],
  validationOptions: DataFieldValidationTypeOption[],
): T[] => {
  const betweenRow = rows.find(
    (row) =>
      isLiveRow(row) &&
      isBetweenValidationType(row.instrumentTaskDataFieldValidationTypeId, validationOptions),
  )
  if (!betweenRow) return rows
  return enforceSingleBetween(rows, betweenRow)
}

// A numeric constraint derived from one acceptable condition. `min`/`max` are inclusive
// bounds (±Infinity for an open side); null means "not yet constraining" (blank/partial row).
interface NumericConstraint {
  min: number
  max: number
}

const toNumber = (raw: string): number | null => {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

// Translates a single acceptable condition into an inclusive numeric interval, or null when
// it does not constrain yet (blank value, non-numeric, or unknown operator). A tiny epsilon
// turns the strict operators (>, <) into inclusive bounds without excluding integers.
const EPSILON = 1e-9

const constraintForRow = (
  row: AcceptableConditionRow,
  validationOptions: DataFieldValidationTypeOption[],
): NumericConstraint | null => {
  const code = validationTypeCodeFor(row.instrumentTaskDataFieldValidationTypeId, validationOptions)
  if (!code) return null

  if (code === 'range_bt') {
    const bounds = parseBetweenBounds(row.value)
    const min = toNumber(bounds.min)
    const max = toNumber(bounds.max)
    if (min === null || max === null) return null
    return { min, max }
  }

  const n = toNumber(row.value)
  if (n === null) return null

  switch (code) {
    case 'range_gt':
      return { min: n + EPSILON, max: Infinity }
    case 'range_gt_eq':
      return { min: n, max: Infinity }
    case 'range_lt':
      return { min: -Infinity, max: n - EPSILON }
    case 'range_lt_eq':
      return { min: -Infinity, max: n }
    case 'equals':
      return { min: n, max: n }
    default:
      return null
  }
}

// Detects acceptable conditions that can never all be satisfied at once. Only conditions
// combined with AND semantics are mutually constraining; the result is the set of row
// indices that participate in an empty intersection. Blank/partial/non-numeric rows are
// ignored (so the editor does not flag a half-typed rule). Returns [] when there is no
// contradiction. Scope: Number fields where numeric operators / Between apply.
//
// Operator awareness (REBUILD-515 fix): the acceptable rules combine with OR by default
// (AcceptableValueEvaluator: "OR (default) = first matching rule wins"), and an OR of
// individually-satisfiable rules is always satisfiable — so a disjoint OR set (e.g.
// "> 16 OR between 5–10") is NOT a contradiction and must not block save. Only an AND set
// (every rule required at once) can be impossible. The operator is stored on the first rule
// and applies to all, mirroring the backend; so unless it is explicitly 'AND', return [].
export const findContradictoryAcceptableConditions = <T extends AcceptableConditionRow>(
  rows: T[],
  validationOptions: DataFieldValidationTypeOption[],
): number[] => {
  // Only "acceptable" conditions constrain the satisfiable range; unacceptable rules exclude
  // values rather than require them, so they can't make the set impossible on their own.
  const indexed = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => isLiveRow(row) && row.isAcceptable !== false)

  // Operator is carried on the first (live, acceptable) rule and applies to all of them.
  // Anything other than an explicit 'AND' means OR semantics, under which the set can never
  // be contradictory on its own.
  const operator = indexed[0]?.row.operatorType
  if (operator?.toUpperCase() !== 'AND') return []

  const constrained = indexed
    .map(({ row, index }) => ({ index, constraint: constraintForRow(row, validationOptions) }))
    .filter(
      (entry): entry is { index: number; constraint: NumericConstraint } =>
        entry.constraint !== null,
    )

  if (constrained.length < 2) return []

  // Intersect all constraints; if the running interval becomes empty, every constraint seen
  // so far is implicated in the contradiction.
  let low = -Infinity
  let high = Infinity
  for (const { constraint } of constrained) {
    low = Math.max(low, constraint.min)
    high = Math.min(high, constraint.max)
  }

  if (low <= high) return []
  return constrained.map((entry) => entry.index)
}

export const hasContradictoryAcceptableConditions = <T extends AcceptableConditionRow>(
  rows: T[],
  validationOptions: DataFieldValidationTypeOption[],
): boolean => findContradictoryAcceptableConditions(rows, validationOptions).length > 0
